'use client';

import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import {AcceptableDocument, isDocument} from './document-like';
import {BLOCKS} from '@contentful/rich-text-types';
import styles from './div.module.scss';
import {ReactNode, useCallback, useEffect} from 'react';

export interface RichDivProps extends GenOptionsProps {
  children?: AcceptableDocument;
  className?: string;
}

export interface GenOptionsProps {
  onDrawerOpenClick?: React.MouseEventHandler<HTMLAnchorElement>;
  setHaveOpenDrawerLink?: (haveLink: boolean) => void;
  classNames?: {
    [k in BLOCKS | 'hyperlink']?: string;
  };
}

type LinkAttributes = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

const OPEN_DRAWER_LINK = 'open-drawer';

const EXTERNAL_LINK_ATTRIBUTES = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const getBlockClasses = (
  classNames: GenOptionsProps['classNames'],
  block: BLOCKS,
) => (classNames?.[block] ? classNames[block] : '');

const genOptions = ({
  onDrawerOpenClick,
  setHaveOpenDrawerLink,
  classNames,
}: GenOptionsProps): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className={getBlockClasses(classNames, BLOCKS.PARAGRAPH)}>
        {children}
      </p>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul
        className={`${styles.list} ${getBlockClasses(classNames, BLOCKS.UL_LIST)}`}
      >
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol
        className={`${styles.orderedList} ${getBlockClasses(classNames, BLOCKS.OL_LIST)}`}
      >
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li
        className={`${styles.item} ${getBlockClasses(classNames, BLOCKS.LIST_ITEM)}`}
      >
        {children}
      </li>
    ),
    hyperlink: (node, children) => {
      const uri = node.data.uri;
      const additionaAttributes: LinkAttributes = isExternal(uri)
        ? EXTERNAL_LINK_ATTRIBUTES
        : {};

      const isOpenDrawerLink = uri === OPEN_DRAWER_LINK;

      const onClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          event.stopPropagation();

          if (isOpenDrawerLink) {
            event.preventDefault();
            onDrawerOpenClick?.(event);
          }
        },
        [isOpenDrawerLink, onDrawerOpenClick],
      );

      useEffect(() => {
        setHaveOpenDrawerLink?.(isOpenDrawerLink);
      }, [isOpenDrawerLink, setHaveOpenDrawerLink]);

      return (
        <a
          className={`${styles.link} ${getBlockClasses(classNames, 'hyperlink' as keyof GenOptionsProps['classNames'])}`}
          href={node.data.uri}
          onClick={onClick}
          {...additionaAttributes}
        >
          {children}
        </a>
      );
    },
  },
  renderText: text => {
    return text
      .split('\n')
      .reduce((acc: ReactNode[], textSegment: string, index: number) => {
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
        return [
          ...acc,
          index > 0 && <br key={`break-${index}`} />,
          textSegment,
        ];
      }, []);
  },
});

/** Will return true if url starts from "http", "https", "://" */
function isExternal(uri: string): boolean {
  return uri.startsWith('http') || uri.startsWith('://');
}

/**
 * Text paragraph that renders rich text. Just pass rich text object and it will render
 * @example <RichDiv>{planDetails as any}</RichDiv>
 * */
export const RichDiv = ({children, className, ...props}: RichDivProps) => {
  if (!isDocument(children)) {
    return null;
  }
  const options = genOptions(props);
  return (
    <div className={className}>
      {documentToReactComponents(children, options)}
    </div>
  );
};
